import { animate, AnimationBuilder, AnimationPlayer, keyframes, style } from "@angular/animations";
import { Component, ElementRef, Input, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { Subscription } from "rxjs";
import { debounceTime, startWith } from "rxjs/operators";

interface ValidationError {
    control: AbstractControl;
    controlName: string;
    field: string;
    error: string;
}
import { debounce } from "lodash-es";
import { MatCard, MatCardHeader, MatCardTitle, MatCardContent } from "@angular/material/card";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";

const errorTagsToString = (error) => {
    switch (error) {
        case "max":
            return "greater than maximum.";

        case "min":
            return "less than the minimum.";

        case "invalidCustomer":
            return "is invalid. Please select a valid customer.";

        case "invalidCarrier":
            return "is invalid. Please select a valid carrier.";

        case "blockedCredit":
            return "has blocked credit. Please select another customer.";

        case "blocked":
            return "is blocked. Please select another carrier.";

        case "insuranceExpired":
            return "insurance has expired. Please select another carrier.";

        case "fleetUnavailable":
            return "has been deleted. Select another truck or None before saving.";

        case "maxlength":
            return "exceeds max length.";

        case "pattern":
            return "is invalid.";

        default:
            return error;
    }
};

const controlToLabelValue = (name) => {
    let formControl = $("[formControlName='" + name + "']");
    let labelText = formControl.closest("div").find("label").first().text() ?? name;

    switch (name) {
        case "Carrier":
            return name;
        case "CarrierFleetID":
            return "Assigned Truck";
        default:
            return labelText.replace(/[^A-Za-z0-9]/g, "");
    }
};

@Component({
    selector: "validation-summary",
    templateUrl: "./validation-summary.component.html",
    styleUrls: ["./validation-summary.component.scss"],
    imports: [MatCard, MatCardHeader, MatCardTitle, MatIcon, MatTooltip, MatCardContent],
})
export class ValidationSummaryComponent implements OnInit, OnDestroy {
    @Input() form: FormGroup;
    @Input() name: string;
    errors: ValidationError[] = [];
    subs: Subscription[] = [];
    private player: AnimationPlayer;

    constructor(
        private el: ElementRef,
        private animationBuilder: AnimationBuilder
    ) {}

    ngOnInit() {
        if (!this.form) return;
        this.form.updateValueAndValidity();
        this.getErrors();

        Object.keys(this.form.controls).forEach((name) => {
            this.subs.push(
                this.form
                    .get(name)
                    .valueChanges.pipe(startWith(""), debounceTime(500))
                    .subscribe((value) => {
                        this.getErrors();
                    })
            );
        });
    }

    ngOnDestroy() {
        this.subs.forEach((sub) => sub.unsubscribe());
        if (this.player) {
            this.player.destroy();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes["form"].currentValue !== changes["form"].previousValue) {
            this.getErrors();
        }
    }

    focusCard(event) {
        event.stopPropagation();
        this.focusError(event, this.errors[0]);
        var invalid = this.getInvalidElements(this.errors[0], this.el.nativeElement);
        var invalidCard = this.getInvalidCard(invalid);

        invalidCard.scrollIntoView({ block: "center", behavior: "smooth" });

        let animationFactory = this.animationBuilder.build([
            style({}),
            animate(
                "500ms ease-out",
                keyframes([
                    style({ transform: "scale(1)", zIndex: 999, backgroundColor: "*", offset: 0 }),
                    style({ transform: "scale(1.1)", zIndex: 999, backgroundColor: "#ff4646", offset: 0.65 }),
                    style({ transform: "scale(1.05)", zIndex: 999, backgroundColor: "#ff4646", offset: 0.85 }),
                    style({ transform: "scale(1)", zIndex: 999, backgroundColor: "*", offset: 1 }),
                ])
            ),
        ]);

        this.player = animationFactory.create(invalidCard);
        this.player.play();

        this.player.onDone(() => {
            this.player.destroy();
            this.player = null;
        });
    }

    getInvalidCard(element) {
        let invalid = element.querySelector("mat-card");
        if (invalid == null || invalid == undefined) {
            return this.getInvalidCard(element.parentElement);
        }
        return invalid;
    }

    focusError(event, e) {
        event.stopPropagation();
        var invalid = this.getInvalidElements(e, this.el.nativeElement);
        invalid.scrollIntoView({ block: "center" });
        invalid.focus();
    }

    getInvalidElements(error, element) {
        let invalid = element.querySelector(".ng-invalid[formControlName='" + error.controlName + "']");
        if (invalid == null || invalid == undefined) {
            return this.getInvalidElements(error, element.parentElement);
        }
        return invalid;
    }

    getErrors = debounce(
        (): any => {
            this.errors = [];
            let controlNames = Object.keys(this.form.controls);
            for (let i = 0; i < controlNames.length; i++) {
                let control: AbstractControl = this.form["controls"][controlNames[i]];
                if (control.status == "INVALID") {
                    Object.keys(control.errors).forEach((err) => {
                        this.errors.push({
                            control: control,
                            controlName: controlNames[i],
                            field: controlToLabelValue(controlNames[i]),
                            error: errorTagsToString(err),
                        });
                    });
                }
            }
        },
        1000,
        { leading: true, trailing: true }
    );
}
